'use client';

import useSWR from 'swr';
import { useCallback, useMemo } from 'react';

// ---- Event types matching backend consultation_pubsub events ----

export interface ConsultationPhaseEvent {
  consultation: true;
  type: 'phase';
  phase: 'discovery' | 'building_docs' | 'planning' | 'enriching' | 'complete';
  fn_count?: number;
  scr_count?: number;
}

export interface ConsultationDiscoveryEvent {
  consultation: true;
  type: 'discovery_progress';
  domain: string;
  tools_found: string[];
  completed: number;
  total: number;
}

export interface ConsultationThinkingEvent {
  consultation: true;
  type: 'planning_thinking';
  content: string;
}

export interface ConsultationPlanChunkEvent {
  consultation: true;
  type: 'plan_chunk';
  content: string;
}

export interface PlanStep {
  id: string;
  type: string;
  description: string;
  status: string;
  agents?: { task: string }[];
  functions?: string[];
  templates?: string[];
}

export interface ConsultationPlanStepAddedEvent {
  consultation: true;
  type: 'plan_step_added';
  step: PlanStep;
}

export interface ConsultationPlanAgentAddedEvent {
  consultation: true;
  type: 'plan_agent_added';
  step_id: string;
  agent: { task: string };
}

export interface ConsultationPlanStepCompleteEvent {
  consultation: true;
  type: 'plan_step_complete';
  step_id: string;
}

export type ConsultationEvent =
  | ConsultationPhaseEvent
  | ConsultationDiscoveryEvent
  | ConsultationThinkingEvent
  | ConsultationPlanChunkEvent
  | ConsultationPlanStepAddedEvent
  | ConsultationPlanAgentAddedEvent
  | ConsultationPlanStepCompleteEvent;

// ---- Store shape ----

export interface ConsultationState {
  phase: string | null;
  discoveries: ConsultationDiscoveryEvent[];
  thinkingBuffer: string;
  fnCount: number;
  scrCount: number;
  discoveryTotal: number;
  discoveryCompleted: number;
  planMarkdown: string;
  steps: PlanStep[];
}

const initialState: ConsultationState = {
  phase: null,
  discoveries: [],
  thinkingBuffer: '',
  fnCount: 0,
  scrCount: 0,
  discoveryTotal: 0,
  discoveryCompleted: 0,
  planMarkdown: '',
  steps: [],
};

// ---- SWR-backed global store (same pattern as useArtifact) ----

export function useConsultationEvents() {
  const { data: state, mutate: setState } = useSWR<ConsultationState>(
    'consultation-events',
    null,
    { fallbackData: initialState },
  );

  const consultation = useMemo(() => state ?? initialState, [state]);

  const pushEvent = useCallback(
    (event: ConsultationEvent) => {
      setState((current) => {
        const s = current ?? initialState;
        switch (event.type) {
          case 'phase':
            return {
              ...s,
              phase: event.phase,
              fnCount: event.fn_count ?? s.fnCount,
              scrCount: event.scr_count ?? s.scrCount,
            };
          case 'discovery_progress':
            return {
              ...s,
              discoveries: [...s.discoveries, event],
              discoveryCompleted: event.completed,
              discoveryTotal: event.total,
            };
          case 'planning_thinking':
            return {
              ...s,
              thinkingBuffer: s.thinkingBuffer + event.content,
            };
          case 'plan_chunk':
            return {
              ...s,
              planMarkdown: s.planMarkdown + event.content,
            };
          case 'plan_step_added':
            return {
              ...s,
              steps: (() => {
                const existingIndex = s.steps.findIndex(
                  (step) => step.id === event.step.id
                );
                if (existingIndex === -1) {
                  return [...s.steps, event.step];
                }
                const next = [...s.steps];
                next[existingIndex] = {
                  ...next[existingIndex],
                  ...event.step,
                };
                return next;
              })(),
            };
          case 'plan_agent_added':
            return {
              ...s,
              steps: s.steps.map((step) =>
                step.id === event.step_id
                  ? {
                      ...step,
                      agents: (step.agents ?? []).some(
                        (agent) => agent.task === event.agent.task
                      )
                        ? (step.agents ?? [])
                        : [...(step.agents ?? []), event.agent],
                    }
                  : step
              ),
            };
          case 'plan_step_complete':
            return {
              ...s,
              steps: s.steps.map((step) =>
                step.id === event.step_id
                  ? { ...step, status: 'completed' }
                  : step
              ),
            };
          default:
            return s;
        }
      }, { revalidate: false });
    },
    [setState],
  );

  const reset = useCallback(() => {
    setState(initialState, { revalidate: false });
  }, [setState]);

  return useMemo(
    () => ({ consultation, pushEvent, reset }),
    [consultation, pushEvent, reset],
  );
}
